import {
  planMoney,
  planPrice,
  PLANS,
  selfServe,
  type Billing,
  type Company,
  type PlanId,
  t,
} from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { Dialog } from './Dialog';

/** Aprobarea comenzilor, pe scurt, pentru fiecare pachet. */
const APPROVALS = {
  none: t('fără aprobări'),
  single: t('aprobare peste un prag'),
  multi: t('aprobări pe pași și pentru fiecare șantier'),
} as const;

const limit = (n: number | null, one: string, many: string) =>
  n == null ? t('{0} nelimitat', many) : `${n} ${n === 1 ? one : many}`;

/**
 * „Alege pachetul”. Cu plata cu cardul pornită, Start și Echipă se plătesc pe
 * pagina Stripe și pachetul se pune singur; Companie (preț „de la”) și serverele
 * fără Stripe trimit cererea la Procumi. Fără Stripe, Procumi e în „acces timpuriu”
 * (2026-10-27: firma proprietarului nu e încă înființată, deci nu se iau plăți):
 * cererea ajunge la vânzări, care hotărăsc de la caz la caz. Aceleași prețuri
 * ca pe procumi.com/preturi, din `shared/plans.ts`: lei în România, euro în rest.
 */
export function PlanDialog({
  company,
  toast,
  onDone,
  onClose,
}: {
  company: Company;
  toast: Toast;
  onDone: (c: Company) => void;
  onClose: () => void;
}) {
  const initial = (company.planRequest?.plan ?? company.plan ?? 'team') as PlanId;
  const [plan, setPlan] = useState<PlanId>(initial);
  const [billing, setBilling] = useState<Billing>(
    (company.planRequest?.billing ?? company.billing ?? 'monthly') as Billing,
  );
  const [busy, setBusy] = useState(false);

  const chosen = PLANS.find((p) => p.id === plan)!;
  const same = company.plan === plan && company.billing === billing;
  const byCard = company.cardPayments && selfServe(chosen);
  const subscribed = company.subscription != null && company.subscription.status !== 'canceled';
  const price = (p: (typeof PLANS)[number], b: Billing) => planPrice(p, b, company.currency);

  const send = async () => {
    setBusy(true);
    try {
      if (byCard) {
        const r = await api.payPlan({ plan, billing });
        if ('url' in r) {
          // pagina de plată Stripe; de acolo omul se întoarce la `/?plata=ok`
          window.location.assign(r.url);
          return;
        }
        onDone(r.company);
        onClose();
        toast(t('Ai acum pachetul {0}. Diferența se socotește la următoarea factură.', chosen.name));
        return;
      }
      onDone(await api.requestPlan({ plan, billing }));
      onClose();
      toast(
        !company.cardPayments
          ? t('Am primit cererea. Te contactăm în curând.')
          : chosen.quote
            ? t('Am primit cererea. Te sunăm să stabilim prețul.')
            : t('Am primit cererea. Îți trimitem factura pe email.'),
      );
    } catch (e) {
      toast(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <Dialog onClose={onClose}>
      <div className="dlg-h">
        <div>
          <h2>{t('Alege pachetul')}</h2>
          <p className="hint">
            {t(
              'Comparația, comenzile și istoricul de prețuri sunt în fiecare pachet. Diferă câți colegi lucrează în Procumi, câte șantiere ai deschise deodată și aprobarea comenzilor. Prețuri fără TVA.',
            )}
          </p>
        </div>
      </div>
      <div className="dlg-b">
        <div className="seg" role="radiogroup" aria-label={t('Cum plătești')}>
          {(['monthly', 'yearly'] as const).map((b) => (
            <button
              key={b}
              type="button"
              role="radio"
              aria-checked={billing === b}
              onClick={() => setBilling(b)}
            >
              {b === 'monthly' ? t('Lunar') : t('Anual')}
              {b === 'yearly' ? <span className="seg-gift">{t('2 luni gratuite')}</span> : null}
            </button>
          ))}
        </div>

        <div className="plan-pick" role="radiogroup" aria-label={t('Pachetul')}>
          {PLANS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={plan === p.id}
              className="plan-opt"
              onClick={() => setPlan(p.id)}
            >
              <span className="plan-opt-name">
                {p.name}
                {company.plan === p.id ? <span className="pill off">{t('pachetul tău')}</span> : null}
              </span>
              <span className="plan-opt-lim">
                {limit(p.users, t('utilizator'), t('utilizatori'))} ·{' '}
                {limit(p.sites, t('șantier activ'), t('șantiere active'))} · {t(APPROVALS[p.approvals])}
                {p.id === 'company' ? t(' · suport prioritar') : ''}
              </span>
              <span className="plan-opt-price">
                {billing === 'yearly' ? (
                  <>
                    {p.quote ? t('de la ') : ''}
                    <b>{planMoney(price(p, 'yearly'))}</b> {t('/ an')}
                    <small className="save">
                      {t(
                        'economisești {0}',
                        planMoney({
                          ...price(p, 'yearly'),
                          amount: price(p, 'monthly').amount * 12 - price(p, 'yearly').amount,
                        }),
                      )}
                    </small>
                  </>
                ) : (
                  <>
                    {p.quote ? t('de la ') : ''}
                    <b>{planMoney(price(p, 'monthly'))}</b> {t('/ lună')}
                  </>
                )}
              </span>
            </button>
          ))}
        </div>

        <p className="hint" style={{ margin: 0 }}>
          {byCard
            ? subscribed
              ? t(
                  'Schimbarea se face pe loc, pe cardul de acum. Diferența de preț se socotește la următoarea factură.',
                )
              : t(
                  'Plătești cu cardul pe pagina securizată Stripe; pachetul se pune imediat, iar factura vine pe email.{0} Anulezi oricând.',
                  company.trialEndsAt && Date.parse(company.trialEndsAt) > Date.now() + 2 * 86_400_000
                    ? t(' Prima plată se ia abia când se termină proba gratuită.')
                    : '',
                )
            : !company.cardPayments
              ? t(
                  'Procumi e în acces timpuriu și încă nu ia plăți online. Trimite cererea și te contactăm pentru pasul următor.',
                )
              : chosen.quote
                ? t(
                    'Prețul pentru Companie îl stabilim după un demo, după mărimea firmei. Trimite cererea și te sunăm.',
                  )
                : t(
                    'Îți trimitem factura pe email, cu datele din „Contul firmei”. Pachetul se activează când primim plata.',
                  )}
        </p>
      </div>
      <div className="dlg-f">
        <button className="btn" type="button" onClick={onClose}>
          {t('Renunță')}
        </button>
        <button className="btn primary" type="button" disabled={busy || same} onClick={() => void send()}>
          {busy
            ? byCard
              ? t('Se deschide plata…')
              : t('Se trimite…')
            : byCard
              ? subscribed
                ? t('Trec la {0}', chosen.name)
                : t('Plătesc {0} cu cardul', chosen.name)
              : t('Cer pachetul {0}', chosen.name)}
        </button>
      </div>
    </Dialog>
  );
}
